/** Text input (U5): glass field chrome, label/hint/error, prefix/suffix. */
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cx } from './cx';
import { Field } from './Field';
import type { ControlSize } from './Button';

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  size?: ControlSize;
  prefix?: ReactNode;
  suffix?: ReactNode;
  full?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hint,
    error,
    size = 'md',
    prefix,
    suffix,
    full = false,
    className,
    id,
    required,
    disabled,
    ...rest
  },
  ref,
): JSX.Element {
  const autoId = useId();
  const inputId = id ?? autoId;
  const describedById = hint != null || error ? `${inputId}-desc` : undefined;

  return (
    <Field
      htmlFor={inputId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      full={full}
      className={className}
      describedById={describedById}
    >
      <span
        className={cx(
          'bl-control',
          `bl-control--${size}`,
          full && 'bl-control--full',
          Boolean(error) && 'is-invalid',
          disabled && 'is-disabled',
        )}
      >
        {prefix != null && <span className="bl-control__affix">{prefix}</span>}
        <input
          ref={ref}
          id={inputId}
          className="bl-control__input"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedById}
          required={required}
          disabled={disabled}
          {...rest}
        />
        {suffix != null && <span className="bl-control__affix">{suffix}</span>}
      </span>
    </Field>
  );
});
